import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
// import axios from "axios";
// import { ENV } from "../../utils/env";
import { getIntegrationConfig, getIntegrationEndpoint, DEFAULT_TOP_ITEMS } from "../../config/integrationConfig";

const getValueFromPath = (obj, path) => {
  if (!obj || !path || typeof path !== 'string') return undefined;
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};


const IntegrationStats = ({ integrationType }) => {
  const integrationConfig = useMemo(() => getIntegrationConfig(integrationType), [integrationType]);

  const getStatsEndpoint = () => {
    if (integrationType) {
      const config = getIntegrationConfig(integrationType);
      if (config) {
        const endpoint = getIntegrationEndpoint(integrationType, 'stats');
        // getIntegrationEndpoint can return an object or a string
        return typeof endpoint === 'object' ? endpoint.url : endpoint;
      }
    }
    return 'api/integrations/stats';
  };

  // Single fetch for stats (no polling needed)
  const [cards, setCards] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Generate dummy data based on integration type
  const generateDummyStats = (type) => {
    const baseStats = {
      scan_status: {
        completed_scans: 156,
        running_scans: 12,
        failed_scans: 3,
        hours_period: 24,
        completed: 156,
        running: 12,
        failed: 3
      },
      critical_issues: {
        active_tokens: 342,
        total_tokens: 1248,
        unvalidated_tokens: 89,
        resolved: 817,
        ignored_active_tokens: 12,
        ignored_unvalidated_tokens: 5
      },
      critical_findings: {
        active: 342,
        total: 1248,
        unvalidated: 89,
        resolved: 817
      },
      total_repos: {
        total: 150,
        onboarded: 142,
        not_onboarded: 8
      },
      total_integrations: {
        total: 150,
        active: 142,
        inactive: 8
      },
      vulnerable_sources: {
        sources: [
          { detector_type: "API Key", count: 234 },
          { detector_type: "AWS Access Key", count: 187 },
          { detector_type: "Private Key", count: 156 },
          { detector_type: "GitHub Token", count: 98 },
          { detector_type: "JWT Token", count: 67 },
          { detector_type: "Database URL", count: 45 },
          { detector_type: "OAuth Token", count: 32 }
        ]
      }
    };

    // Type-specific adjustments
    switch (type) {
      case 's3':
        return {
          ...baseStats,
          total_integrations: {
            total: 87,
            active: 82,
            inactive: 5
          },
          encryption_stats: {
            types: [
              { type: "AES-256", count: 45 },
              { type: "AES-128", count: 23 },
              { type: "None", count: 14 },
              { type: "AWS-KMS", count: 5 }
            ]
          }
        };
      case 'github':
        return {
          ...baseStats,
          total_repos: {
            total: 234,
            onboarded: 221,
            not_onboarded: 13
          },
          vulnerable_sources: {
            sources: [
              { detector_type: "GitHub Token", count: 187 },
              { detector_type: "API Key", count: 134 },
              { detector_type: "AWS Access Key", count: 98 },
              { detector_type: "Private Key", count: 76 },
              { detector_type: "JWT Token", count: 54 }
            ]
          }
        };
      case 'bitbucket':
      case 'gitlab':
        return {
          ...baseStats,
          total_repos: {
            total: 112,
            onboarded: 107,
            not_onboarded: 5
          },
          vulnerable_sources: {
            sources: [
              { detector_type: "API Key", count: 98 },
              { detector_type: "Access Token", count: 76 },
              { detector_type: "Private Key", count: 54 }
            ]
          }
        };
      case 'aws':
        return {
          ...baseStats,
          total_integrations: {
            total: 67,
            active: 64,
            inactive: 3
          }
        };
      case 'azure':
        return {
          ...baseStats,
          total_integrations: {
            total: 45,
            active: 42,
            inactive: 3
          }
        };
      case 'gcs':
        return {
          ...baseStats,
          total_integrations: {
            total: 56,
            active: 53,
            inactive: 3
          }
        };
      case 'teams':
        return {
          ...baseStats,
          total_integrations: {
            total: 23,
            active: 21,
            inactive: 2
          },
          scan_status: {
            completed_scans: 89,
            running_scans: 5,
            failed_scans: 1,
            hours_period: 24,
            completed: 89,
            running: 5,
            failed: 1
          }
        };
      default:
        return baseStats;
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Use dummy data based on integration type
        const dummyData = generateDummyStats(integrationType);
        setCards(dummyData);
        setError(null);
      } catch (err) {
        const errorMessage = err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch stats";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [integrationType]);

  // Fallback for generic stats page
  const integrationsByType = useMemo(() => {
    if (!cards?.vulnerable_sources?.sources) return [];
    return [...cards.vulnerable_sources.sources]
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .slice(0, DEFAULT_TOP_ITEMS);
  }, [cards]);

  const scanStatus = useMemo(() => {
    const status = cards?.scan_status;
    if (!status) {
      return { completed: 0, running: 0, failed: 0, hours: 24 };
    }
    return {
      completed: Number(status.completed_scans || 0),
      running: Number(status.running_scans || 0),
      failed: Number(status.failed_scans || 0),
      hours: Number(status.hours_period || 24),
    };
  }, [cards]);

  if (integrationConfig && integrationConfig.statsCards) {
    return (
      <div className="mb-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {integrationConfig.statsCards.map((cardConfig) => {
            const mainValue = getValueFromPath(cards, cardConfig.dataPath);
            
            let subText = cardConfig.subText;
            if (subText) {
              if (cardConfig.subTextPath) {
                const subTextValue = getValueFromPath(cards, cardConfig.subTextPath);
                subText = subText.replace('{total}', Intl.NumberFormat().format(subTextValue || 0));
              }
              if (subText.includes('{hours}')) {
                const hours = getValueFromPath(cards, 'scan_status.hours_period') || 
                              getValueFromPath(cards, 'scan_status.hours') || 
                              24;
                subText = subText.replace('{hours}', hours);
              }
            }

            return (
              <Card key={cardConfig.key} className="bg-white border border-[#441a9a] shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">{cardConfig.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded" />
                  ) : error ? (
                    <div className="text-sm text-red-600">{error}</div>
                  ) : (
                    <>
                      {cardConfig.type === 'tag_list' ? (
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const limit = cardConfig.maxItems || DEFAULT_TOP_ITEMS;
                            const items = (mainValue || []).slice(0, limit);
                            if (items.length === 0) {
                              return <div className="text-sm text-gray-500">No data</div>;
                            }
                            return items.map((item, index) => (
                              <div
                                key={index}
                                className="px-2 py-1 rounded border border-[#441a9a] text-[#441a9a] text-xs bg-white"
                                title={`${item[cardConfig.displayKey]}: ${item[cardConfig.countKey]}`}
                              >
                                {item[cardConfig.displayKey]}: {Intl.NumberFormat().format(item[cardConfig.countKey] || 0)}
                              </div>
                            ));
                          })()}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-3">
                            <div className="text-3xl font-bold text-gray-900">
                              {Intl.NumberFormat().format(mainValue || 0)}
                            </div>
                            {cardConfig.badge && (
                              <span className={`bg-${cardConfig.badge.color}-100 text-${cardConfig.badge.color}-800 text-sm font-medium px-2.5 py-0.5 rounded-full`}>
                                {cardConfig.badge.text}
                              </span>
                            )}
                          </div>
                          {subText && (
                            <div className="mt-2 text-sm text-gray-600">
                              {subText}
                            </div>
                          )}
                          {cardConfig.subStats && (
                            <div className="mt-1 text-xs text-gray-500">
                              {cardConfig.subStats.map((sub, index) => {
                                const subValue = getValueFromPath(cards, sub.dataPath);
                                return (
                                  <span key={sub.key}>
                                    {index > 0 && ' • '}
                                    {sub.label} {Intl.NumberFormat().format(subValue || 0)}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Repositories */}
        <Card className="bg-white border border-[#441a9a] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Repositories</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-24 bg-gray-200 animate-pulse rounded" />
            ) : error ? (
              <div className="text-sm text-red-600">{error}</div>
            ) : (
              <>
                <div className="text-3xl font-bold text-gray-900">
                  {Intl.NumberFormat().format(cards?.total_repos?.total || 0)}
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  <div>Onboarded: {Intl.NumberFormat().format(cards?.total_repos?.onboarded || 0)}</div>
                  <div>Not Onboarded: {Intl.NumberFormat().format(cards?.total_repos?.not_onboarded || 0)}</div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Integration types */}
        <Card className="bg-white border border-[#441a9a] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Top Vulnerabilities</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <div className="h-6 w-32 bg-gray-200 animate-pulse rounded" />
                <div className="h-6 w-28 bg-gray-200 animate-pulse rounded" />
                <div className="h-6 w-24 bg-gray-200 animate-pulse rounded" />
              </div>
            ) : error ? (
              <div className="text-sm text-red-600">{error}</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {integrationsByType.map((integration) => (
                  <div
                    key={integration.detector_type}
                    className="px-2 py-1 rounded border border-[#441a9a] text-[#441a9a] text-xs bg-white"
                    title={`${integration.detector_type}: ${integration.count}`}
                  >
                    {integration.detector_type}: {Intl.NumberFormat().format(integration.count || 0)}
                  </div>
                ))}
                {integrationsByType.length === 0 && (
                  <div className="text-sm text-gray-500">No data</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scan status */}
        <Card className="bg-white border border-[#441a9a] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Scan status</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-24 bg-gray-200 animate-pulse rounded" />
            ) : error ? (
              <div className="text-sm text-red-600">{error}</div>
            ) : (
              <>
                <div className="text-3xl font-bold text-gray-900">{Intl.NumberFormat().format(scanStatus.completed)}</div>
                <div className="mt-2 text-sm text-gray-600">
                  Completed in last {scanStatus.hours}h
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Running {Intl.NumberFormat().format(scanStatus.running)}  Failed {Intl.NumberFormat().format(scanStatus.failed)}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Critical findings */}
        <Card className="bg-white border border-[#441a9a] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Critical findings</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-24 bg-gray-200 animate-pulse rounded" />
            ) : error ? (
              <div className="text-sm text-red-600">{error}</div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="text-3xl font-bold text-gray-900">{Intl.NumberFormat().format(cards?.critical_issues?.active_tokens || 0)}</div>
                  <span className="bg-red-100 text-red-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
                    Active
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  of {Intl.NumberFormat().format(cards?.critical_issues?.total_tokens || 0)} Total Findings
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Unvalidated {Intl.NumberFormat().format(cards?.critical_issues?.unvalidated_tokens || 0)}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntegrationStats;
